import * as React from "react";
import * as _ from "lodash";
import { Select, Form, List, Message } from "semantic-ui-react";

type ModeOptions = "Remove" | "Add";

export type ModifyDriveParameterBindingsProps = {
  mode: ModeOptions;
  domains: Api.Domains;
  parameters: Api.DriveParameter[];
  driveVersions: Api.DriveVersions;
  updateParameters: (parameters: Api.DriveParameter[]) => void;
  permission: boolean;
};

type ModifyDriveParameterBindingsState = {
  driveVersionId: number;
  domainId: number;
};

/** ModifyDriveParameterBindings component */
class ModifyDriveParameterBindings extends React.Component<
  ModifyDriveParameterBindingsProps,
  ModifyDriveParameterBindingsState
> {
  state = {
    driveVersionId: -1,
    domainId: -1
  };

  componentDidMount(): void {
    const { parameters, driveVersions } = this.props;
    const driveVersionList = _.values(driveVersions);

    if (parameters.length > 0) {
      const domainId = parameters[0].domain.id;
      this.setState(() => ({ domainId }));
    }
    if (driveVersionList.length > 0) {
      const driveVersionId = driveVersionList[0].id;
      this.setState(() => ({ driveVersionId }));
    }
  }

  render(): JSX.Element {
    const { parameters, driveVersions, domains, mode } = this.props;
    const { driveVersionId, domainId } = this.state;

    // Creates drive's version dropdown options
    const mappedOptions = _.values(driveVersions).map(driveVersion => ({
      key: driveVersion.id,
      text: `${driveVersion.year}.${driveVersion.week}.${domains[driveVersion.domain.id].enumName} -
            ${driveVersion.description}`,
      value: driveVersion.id
    }));
    const versionOptions = _.sortBy(mappedOptions, ["text"]).reverse();

    // Creates domain dropdown options for filtering parameters
    const usedDomainIds = _.uniq(parameters.map(parameter => parameter.domain.id));
    const domainOptions = usedDomainIds
      .sort((a, b) => domains[a].enumValue - domains[b].enumValue)
      .map(usedDomainId => ({
        key: usedDomainId,
        text: domains[usedDomainId].enumName + " [" + domains[usedDomainId].enumValue + "]",
        value: usedDomainId
      }));

    // Get parameters which are modified
    let selectedParameters: Api.DriveParameter[] = [];
    if (mode === "Add") {
      // Get parameters in the given domain which do not contain the given version binding
      selectedParameters = parameters.filter(
        parameter =>
          parameter.domain.id === domainId &&
          !parameter.driveParameterBindings.find(
            binding => binding.driveVersion.id === driveVersionId
          )
      );
    } else {
      // Get parameters in the given domain which contain the given version binding
      selectedParameters = parameters.filter(
        parameter =>
          parameter.domain.id === domainId &&
          !!parameter.driveParameterBindings.find(
            binding => binding.driveVersion.id === driveVersionId
          )
      );
    }
    const selectedParametersCount = selectedParameters.length;
    const infoText =
      mode === "Add"
        ? "Add selected binding to the following parameters"
        : "Remove selected binding from the following parameters";

    const selectedParametersNames = selectedParameters.map(
      parameter => `${parameter.enumName} (code = ${parameter.code})`
    );

    return (
      <>
        {mode === "Add" ? (
          <Message
            info={true}
            header="Add version bindings to parameters"
            list={[
              "Select the binding which will be added to all parameters in the selected domain",
              "The list shows parameters to which the binding will be added",
              "Start adding by clicking 'Add binding'"
            ]}
          />
        ) : (
          <Message
            info={true}
            header="Remove version bindings from parameters"
            list={[
              "Select the binding which will be removed from all parameters in the selected domain",
              "The list shows parameters from which the binding will be removed",
              "Start removing by clicking 'Remove binding'"
            ]}
          />
        )}
        <Form.Group>
          <Form>
            <div className="uidropdown">
              <Form.Field
                id="driveVersionsDropdown"
                control={Select}
                label="Version"
                options={versionOptions}
                value={driveVersionId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ driveVersionId: value }))
                }
                disabled={!this.props.permission}
              />
              <Form.Field
                id="driveDomainDropdown"
                control={Select}
                label="Domain"
                options={domainOptions}
                search={true}
                value={domainId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) => this.setState(() => ({ domainId: value }))}
                disabled={!this.props.permission}
              />
            </div>
          </Form>
        </Form.Group>
        <Form.Group className="bindingParameters">
          {selectedParametersCount > 0 ? (
            <>
              <label>{`${infoText} (${selectedParametersCount}):`}</label>
              <div>
                <List items={selectedParametersNames} />
              </div>
            </>
          ) : (
            <>
              <label>Selected binding and domain do not match any parameters.</label>
            </>
          )}
        </Form.Group>
        <>
          {mode === "Add" ? (
            <button
              id="addDriveBindingsButton"
              disabled={selectedParametersCount === 0 || !this.props.permission}
              onClick={() => this.addBindings(selectedParameters)}
              className="bindingsActionBtn"
            >
              Add bindings
            </button>
          ) : (
            <button
              id="removeDriveBindingsButton"
              disabled={selectedParametersCount === 0 || !this.props.permission}
              onClick={() => this.removeBindings(selectedParameters)}
              className="bindingsActionBtn"
            >
              Remove bindings
            </button>
          )}
        </>
      </>
    );
  }

  // Add binding to parameters
  private addBindings = (parameters: Api.DriveParameter[]): void => {
    const { updateParameters, driveVersions } = this.props;
    const { driveVersionId } = this.state;
    const driveVersion = driveVersions[driveVersionId];

    if (driveVersion) {
      const parametersToUpdate = parameters.map(parameter => {
        const newBinding: Api.DriveParameterBinding = {
          driveParameterCode: parameter.code,
          driveVersion: { id: driveVersion.id, url: driveVersion.url }
        };
        return {
          ...parameter,
          driveParameterBindings: [newBinding, ...parameter.driveParameterBindings]
        };
      });
      updateParameters(parametersToUpdate);
    }
  };

  // Remove binding from parameters
  private removeBindings = (parameters: Api.DriveParameter[]): void => {
    const { updateParameters } = this.props;
    const { driveVersionId } = this.state;

    const parametersToUpdate = parameters.map(parameter => ({
      ...parameter,
      driveParameterBindings: parameter.driveParameterBindings.filter(
        binding => binding.driveVersion.id !== driveVersionId
      )
    }));
    updateParameters(parametersToUpdate);
  };
}

export default ModifyDriveParameterBindings;
